import { useState } from "react";
import {
  LogoSymbol,
  LogoWordmark,
  LogoTagline,
  LogoHorizontal,
  LogoCentered,
} from "../components/Logo";

const VARIANTS = [
  {
    id: "editorial",
    name: "Editorial",
    blurb:
      "Instrument Serif — roman 'Blend' + italic 'safe'. Aesop / Rhode / NYT-magazine feel. Quiet weight contrast through italic, not bolding.",
    pairing: "Best for: navbar · social tiles · share OG · marketing hero",
  },
  {
    id: "clinical",
    name: "Clinical",
    blurb:
      "Inter ExtraLight uppercase, very wide tracking. Apple product page / derm clinic feel. Disappears into dense UI.",
    pairing: "Best for: admin · dashboards · clinical reports",
  },
  {
    id: "luxe",
    name: "Luxe",
    blurb:
      "Fraunces variable serif with soft optical sizing. Contemporary luxury beauty-tech (premium DTC). Soft tracking, generous letterforms.",
    pairing: "Best for: splash · paywall · /pro upgrade page · print",
  },
];

// ─── Building blocks ───────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <p className="text-[10px] tracking-[0.32em] uppercase text-ink-mute font-medium mb-3">
      {children}
    </p>
  );
}

function Card({ children, className = "", style = {} }) {
  return (
    <div
      className={`rounded-2xl border border-surface-line bg-white p-8 ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

// ─── Context tiles per variant ─────────────────────────────────────────────

function InstagramTile({ variant }) {
  return (
    <Card
      className="aspect-square flex flex-col items-center justify-center"
      style={{ background: "#FAF7F2" }}
    >
      <LogoCentered
        variant={variant}
        size={88}
        textSize={variant === "clinical" ? 18 : 36}
        taglineSize={variant === "clinical" ? 10 : 13}
      />
    </Card>
  );
}

function NavbarMock({ variant }) {
  return (
    <Card className="p-0 overflow-hidden">
      <div
        className="flex items-center justify-between px-6 py-4 border-b border-surface-line"
        style={{ background: "#FAF7F2" }}
      >
        <LogoHorizontal
          variant={variant}
          size={36}
          textSize={variant === "clinical" ? 14 : 20}
          taglineSize={10}
          withTagline
          gap={14}
        />
        <div className="flex items-center gap-6 text-[13px] text-ink-mute">
          <span>Home</span>
          <span>How it works</span>
          <span>Categories</span>
          <span className="px-4 py-2 rounded-full bg-ink-base text-white text-[12px] font-medium">
            Start free check
          </span>
        </div>
      </div>
    </Card>
  );
}

function MobileAppTile({ variant }) {
  return (
    <Card
      className="aspect-[9/16] max-w-[260px] mx-auto flex flex-col items-center justify-center"
      style={{ background: "#FAF7F2" }}
    >
      <LogoCentered
        variant={variant}
        size={72}
        textSize={variant === "clinical" ? 14 : 28}
        taglineSize={variant === "clinical" ? 9 : 11}
      />
    </Card>
  );
}

function SocialPostTile({ variant }) {
  return (
    <Card
      className="aspect-[1.91/1] flex flex-col justify-between p-10"
      style={{ background: "#111111" }}
    >
      <LogoHorizontal
        variant={variant}
        color="#FAF7F2"
        size={48}
        textSize={variant === "clinical" ? 18 : 28}
        taglineSize={11}
        withTagline
        gap={18}
      />
      <div>
        <p
          className="text-[#FAF7F2]"
          style={{
            fontFamily:
              variant === "luxe"
                ? '"Fraunces", serif'
                : variant === "editorial"
                ? '"Instrument Serif", serif'
                : '"Inter", sans-serif',
            fontSize: variant === "clinical" ? 22 : 36,
            fontWeight: variant === "clinical" ? 300 : 400,
            letterSpacing: variant === "clinical" ? "0.06em" : "-0.01em",
            lineHeight: 1.1,
            maxWidth: 520,
          }}
        >
          Know what your prescription does to your skincare.
        </p>
      </div>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function BrandPreview() {
  const [active, setActive] = useState("editorial");

  return (
    <main className="min-h-screen" style={{ background: "#FAF7F2" }}>
      {/* Hero */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <p className="text-[10px] tracking-[0.32em] uppercase text-ink-mute font-medium mb-4">
          Brand · Typography study
        </p>
        <h1
          className="font-editorial"
          style={{
            fontSize: "clamp(40px, 6vw, 72px)",
            letterSpacing: "-0.015em",
            lineHeight: 1.02,
            color: "#111111",
          }}
        >
          Three ways to set <em>Blendsafe</em>.
        </h1>
        <p
          className="mt-6 max-w-2xl text-ink-mute"
          style={{ fontSize: 17, lineHeight: 1.55 }}
        >
          A premium skincare-tech wordmark should disappear into the brand,
          not announce itself. Below are three carefully-tuned typography
          systems — pick the one that feels right for the room.
        </p>
      </section>

      {/* Variant picker */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 pb-8">
        <div className="flex flex-wrap gap-3">
          {VARIANTS.map((v) => {
            const isActive = active === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setActive(v.id)}
                data-testid={`variant-${v.id}`}
                className={[
                  "px-6 py-3 rounded-full border transition-all",
                  isActive
                    ? "bg-ink-base border-ink-base shadow-sm"
                    : "bg-white text-ink-base border-surface-line hover:border-ink-base/40",
                ].join(" ")}
              >
                <span className="inline-flex items-center gap-4">
                  <LogoWordmark
                    variant={v.id}
                    color={isActive ? "#FAF7F2" : "#111111"}
                    size={20}
                  />
                  <span
                    className="text-[10px] tracking-[0.32em] uppercase font-medium"
                    style={{ color: isActive ? "#a8a39a" : "#8a857c" }}
                  >
                    {v.name}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Active variant — large hero specimen */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 pb-16">
        <Card className="p-12 md:p-20" style={{ background: "#FAF7F2" }}>
          <div className="flex flex-col items-center text-center">
            <LogoSymbol size={140} color="#111111" />
            <div className="mt-10 flex flex-col items-center" style={{ gap: 14 }}>
              <LogoWordmark variant={active} color="#111111" size={active === "clinical" ? 26 : 56} />
              <LogoTagline variant={active} size={active === "clinical" ? 11 : 14} />
            </div>
          </div>
          <hr className="my-12 border-t border-surface-line" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            {VARIANTS.filter((v) => v.id === active).map((v) => (
              <div key={v.id} className="md:col-span-2">
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-mute font-medium mb-3">
                  {v.name}
                </p>
                <p
                  className="font-editorial"
                  style={{
                    fontSize: 22,
                    letterSpacing: "-0.005em",
                    lineHeight: 1.4,
                    color: "#111111",
                  }}
                >
                  {v.blurb}
                </p>
                <p className="mt-4 text-[13px] text-ink-mute" style={{ letterSpacing: "0.02em" }}>
                  {v.pairing}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Context grid */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 pb-24">
        <SectionLabel>In context</SectionLabel>
        <h2
          className="font-editorial mb-10"
          style={{ fontSize: 36, letterSpacing: "-0.01em", color: "#111111" }}
        >
          How it sits in the world.
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <SectionLabel>Instagram · 1:1</SectionLabel>
            <InstagramTile variant={active} />
          </div>

          <div>
            <SectionLabel>Website navbar</SectionLabel>
            <NavbarMock variant={active} />
            <div className="mt-8">
              <SectionLabel>Social post · 1.91:1</SectionLabel>
              <SocialPostTile variant={active} />
            </div>
          </div>

          <div>
            <SectionLabel>Mobile app · splash</SectionLabel>
            <MobileAppTile variant={active} />
          </div>

          <div>
            <SectionLabel>Scale specimen</SectionLabel>
            <Card style={{ background: "#FAF7F2" }}>
              <div className="flex flex-col" style={{ gap: 28 }}>
                {[64, 44, 30, 20, 14].map((size) => (
                  <div key={size} className="flex items-baseline" style={{ gap: 24 }}>
                    <span
                      className="text-ink-mute text-[10px] tabular-nums"
                      style={{ letterSpacing: "0.1em", width: 36 }}
                    >
                      {size}px
                    </span>
                    <LogoWordmark variant={active} color="#111111" size={size} />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Side-by-side comparison */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 pb-24">
        <SectionLabel>Side by side</SectionLabel>
        <h2
          className="font-editorial mb-10"
          style={{ fontSize: 36, letterSpacing: "-0.01em", color: "#111111" }}
        >
          All three, equal weight.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {VARIANTS.map((v) => (
            <Card key={v.id} className="flex flex-col items-center text-center" style={{ background: "#FAF7F2" }}>
              <div className="flex-1 flex flex-col items-center justify-center py-8">
                <LogoCentered variant={v.id} size={64} textSize={v.id === "clinical" ? 16 : 30} taglineSize={11} />
              </div>
              <hr className="w-full border-t border-surface-line my-6" />
              <p className="text-[10px] tracking-[0.32em] uppercase text-ink-mute font-medium">
                {v.name}
              </p>
            </Card>
          ))}
        </div>
      </section>
    </main>
  );
}
